<template>
  <FlexCol class="gap-3">
    <template v-if="showCountry">
      <label for="country" class="fw-bold">Business Location</label>
      <select id="country" v-model="selectedCountryCode" class="form-select">
        <option value="" disabled>Select country</option>
        <option v-for="item in countries" :key="item.code" :value="item.code">
          {{ item.country }}
        </option>
      </select>
    </template>
    <label for="timezone" class="fw-bold">Timezone</label>
    <DropdownSearch
      v-model="selectedTimezone"
      :suggestion="countryTimeZones"
      objKey="zone"
      class="mb-2"
      @changed="isDirty = true"
    />
  </FlexCol>
</template>
<script setup>
import FlexCol from "@/layout/FlexCol.vue";
import { computed, onMounted, ref, watch } from "vue";
import { DateTime } from "luxon";
import DropdownSearch from "@/reusables/DropdownSearch.vue";
import { isObjectEmpty, useBrandStore } from "@/stores/brand";
import { useQuery } from "@urql/vue";
import { COUNTRIES } from "@/constants/graphql";
import { timezones } from "country-data-list";

import axios from "axios";
import { getCurrencyData } from "country-currency-utils";

const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  showCountry: {
    type: Boolean,
    default: false,
  },
  country: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(["update"]);

const selectedTimezone = ref(null);
const selectedCountry = ref(null);
const deviceCountry = ref(null);
const isDirty = ref(false);

const selectedCountryCode = computed({
  get: () => selectedCountry.value?.code ?? "",
  set: (code) => {
    selectedCountry.value =
      countries.value.find((country) => country.code === code) ?? null;
  },
});

const countries = ref([
  { code: "MY", country: "Malaysia", currency: "MYR" },
  { code: "ID", country: "Indonesia", currency: "IDR" },
  { code: "SG", country: "Singapore", currency: "SGD" },
  { code: "AUS", country: "Australia", currency: "AUD" },
]);

const countryQuery = useQuery({
  query: COUNTRIES,
});

watch(countryQuery.fetching, (fetching) => {
  if (!fetching && countryQuery.data.value.countries) {
    countries.value = countryQuery.data.value.countries.map((country) => ({
      code: country.code,
      country: country.name,
      currency: country.spendingCurrency.code,
    }));
  }
});

const zones = computed(() => {
  const timeZones = Intl.supportedValuesOf("timeZone");

  return timeZones.map((zone) => ({
    label: `${DateTime.now().setZone(zone).toFormat("z")} ${
      "(UTC " + DateTime.now().setZone(zone).toFormat("Z") + ")"
    }`,
    zone: DateTime.now().setZone(zone).toFormat("z"),
    utc: DateTime.now().setZone(zone).toFormat("Z"),
  }));
});

const countryTimeZones = computed(() => {
  if (isObjectEmpty(selectedCountry.value)) return zones.value;

  const countryTz = timezones.getTimezonesByCountry(selectedCountry.value.code);

  return zones.value.filter((tz) => {
    return countryTz.some((ctz) => {
      if (selectedCountry.value.code === "MY") {
        return tz.zone === "Asia/Kuala_Lumpur";
      }

      return tz.zone === ctz;
    });
  });
});

onMounted(async () => {
  try {
    await axios.get("https://api.country.is").then((response) => {
      deviceCountry.value = response.data.country;
    });
  } catch (error) {
    console.error("Error fetching data:", error);
  }

  const deviceCountryExists = countries.value.find(
    (country) => country.code === deviceCountry.value
  );

  if (props.country && !isObjectEmpty(props.country)) {
    selectedCountry.value = props.country;
    selectedTimezone.value = countryTimeZones.value[0];
  } else if (deviceCountryExists) {
    selectedCountry.value = deviceCountryExists;
    selectedTimezone.value = countryTimeZones.value[0];
  }
});

watch(
  () => props.country,
  (country) => {
    if (!isDirty.value && country && !isObjectEmpty(country)) {
      selectedCountry.value = country;
      selectedTimezone.value = countryTimeZones.value[0];
    }
    emit("update", {
      timezone: selectedTimezone.value,
      country: selectedCountry.value,
    });
  }
);

watch(
  () => [selectedTimezone.value, selectedCountry.value],
  () => {
    emit("update", {
      timezone: selectedTimezone.value,
      country: selectedCountry.value,
    });
  }
);
</script>
